<template>
    <div>
        <v-row>
            <v-col>
                <h3>File system usage</h3>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <v-progress-linear indeterminate v-if="fetchingFileUsages"></v-progress-linear>
                <file-quota-usage-chart v-else :chartData="chartData" />
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <h3>File quotas</h3>
            </v-col>
        </v-row>

        <template v-if="fetchingFileQuotas">
            <v-row v-for="i in 3" :key="`skeleton-${i}`">
                <v-col cols="12" sm="6" lg="3" xl="3" v-for="j in 4" :key="`skeleton-card-${j}`">
                    <v-skeleton-loader type="card" class="ma-1"></v-skeleton-loader>
                </v-col>
            </v-row>
        </template>
        <template v-else>
            <template v-for="quota in quotas">
                <v-row :key="`header-${quota.name}`">
                    <v-col>
                        <h3 class="text-capitalize">{{ quota.name }}</h3>
                    </v-col>
                </v-row>
                <v-row :key="`body-${quota.name}`">
                    <v-col cols="12" sm="6" lg="3" xl="3" v-for="subQuota in quota.value" :key="`sub-${subQuota.name}`">
                        <v-card flat elevation="3" class="ma-1">
                            <v-card-title>
                                {{ fileTypeLabel(subQuota.name) }}
                            </v-card-title>

                            <v-card-text>
                                <div class="px-6 ma-auto text-center text-h5 secondary--text">{{ bytesToGB(subQuota.value) }} GB</div>
                                <div class="px-7 text-center" v-if="quota.name === 'other'"></div>
                                <div class="px-7 py-4 text-center" v-else-if="fetchingFileUsages">
                                    <v-progress-linear indeterminate></v-progress-linear>
                                </div>
                                <div class="px-7 pt-2" v-else-if="usages[quota.name]?.usage[subQuota.name]">
                                    <v-progress-linear
                                        :value="usages[quota.name].usage[subQuota.name]?.usage_pct"
                                        :color="usageColor(usages[quota.name].usage[subQuota.name]?.usage_pct)"
                                        rounded
                                        class="my-2"
                                        height="8"></v-progress-linear>
                                    <div class="d-flex text--secondary">
                                        <span>{{ usages[quota.name].usage[subQuota.name]?.usage_pct }}% used</span>
                                        <v-spacer></v-spacer>
                                        <strong>{{ bytesToGB(usages[quota.name].usage[subQuota.name]?.usage) }} GB</strong>
                                        /
                                        <span>{{ bytesToGB(subQuota.value) }} GB</span>
                                    </div>
                                </div>
                                <div v-else class="pt-2 text-center">No usage data to show</div>
                            </v-card-text>
                            <v-card-actions>
                                <file-quota-edit-dialog
                                    @success="fetchAll()"
                                    :quota="subQuota.value"
                                    :instance="quota.name"
                                    :type="subQuota.name"
                                    :max-quota="maxQuota">
                                    <v-btn text color="primary">Edit</v-btn>
                                </file-quota-edit-dialog>
                                <v-spacer></v-spacer>
                                <file-quota-instance-usages-dialog v-if="quota.name === 'other'" :usages="usages[quota.name]" :type="subQuota.name">
                                    <v-btn text>
                                        View usage
                                        <v-icon right small>mdi-open-in-new</v-icon>
                                    </v-btn>
                                </file-quota-instance-usages-dialog>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </template>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import fileQuotas from '@/mixins/fileQuotas'

const FileQuotaEditDialog = () => import('@/modules/space/components/FileQuotaEditDialog.vue')
const FileQuotaInstanceUsagesDialog = () => import('@/modules/space/components/FileQuotaInstanceUsagesDialog.vue')
const FileQuotaUsageChart = () => import('@/components/charts/fileQuotaUsageChart.vue')

export default {
    name: 'FileQuotas',
    mixins: [fileQuotas],
    props: {
        spaceId: {
            type: [String, Number],
            default: null
        },
        spaceType: {
            type: Number,
            default: null
        }
    },
    components: {
        FileQuotaEditDialog,
        FileQuotaInstanceUsagesDialog,
        FileQuotaUsageChart
    },
    data() {
        return {
            group: null,
            quotas: [],
            usages: {},
            maxQuota: 0,
            fetchingFileQuotas: false,
            fetchingFileUsages: false,

            chartData: {
                labels: [''],
                datasets: [
                    {
                        label: 'Workspace',
                        data: [],
                        backgroundColor: '#2196F3CC',
                        borderRadius: { topLeft: 10, topRight: 0, bottomLeft: 10, bottomRight: 0 },
                        borderSkipped: false
                    },
                    {
                        label: 'Personal',
                        data: [],
                        backgroundColor: '#9c27b0CC'
                    },
                    {
                        label: 'Application',
                        data: [],
                        backgroundColor: '#11cf82CC',
                        borderRadius: { topLeft: 0, topRight: 10, bottomLeft: 0, bottomRight: 10 },
                        borderSkipped: false
                    }
                ]
            }
        }
    },
    created() {
        this.fetchAll()
    },
    methods: {
        fetchAll() {
            if (!this.resolvedSpaceId) {
                this.quotas = []
                this.usages = {}
                return
            }
            this.fetchQuotas()
            this.fetchUsages()
        },
        async fetchQuotas() {
            try {
                this.fetchingFileQuotas = true

                const { data } = await this.$axios.get(`/spaces/${this.resolvedSpaceId}/quotas`)
                this.maxQuota = data.space_admin_max_quota_bytes

                // Define the sort order
                const sortOrder = ['master', 'distributed', 'other']
                const subQuotaSortOrder = ['global', 'files', 'home', 'lib']

                // Filter out 'distributed' in non education spaces
                let quotasObj = data.quotas
                if (!this.isEducationSpace) {
                    quotasObj = Object.fromEntries(Object.entries(data.quotas).filter(([quotaName]) => quotaName !== 'distributed'))
                }

                // Convert to array and sort
                this.quotas = Object.entries(quotasObj)
                    .map(([name, value]) => ({
                        name,
                        value: Object.entries(value)
                            .map(([subName, subValue]) => ({ name: subName, value: subValue }))
                            .sort((a, b) => subQuotaSortOrder.indexOf(a.name) - subQuotaSortOrder.indexOf(b.name))
                    }))
                    .sort((a, b) => sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name))
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: error.response?.data?.debug || 'There was an error fetching file quota information',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.fetchingFileQuotas = false
        },
        async fetchUsages() {
            try {
                this.fetchingFileUsages = true
                const { data } = await this.$axios.get(`/spaces/${this.resolvedSpaceId}/quotas/usage`)
                this.usages = data.instance_fs_usage

                this.chartData.datasets[0].data = [this.aggregatedUsage('files')]
                this.chartData.datasets[1].data = [this.aggregatedUsage('home')]
                this.chartData.datasets[2].data = [this.aggregatedUsage('lib')]
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: error.response?.data?.debug || 'There was an error fetching file quota usage information',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.fetchingFileUsages = false
        },
        aggregatedUsage(type) {
            let totalUsage = 0
            const master = this.usages?.master
            const distributed = this.usages?.distributed

            totalUsage += master?.usage[type]?.usage || 0
            totalUsage += distributed?.usage[type]?.usage || 0
            totalUsage += this.aggregatedOtherUsage(type)
            return totalUsage
        },
        aggregatedOtherUsage(type) {
            let otherUsage = 0
            const other = this.usages.other
            for (const instance in other) {
                if (other[instance]?.usage[type]) {
                    otherUsage += other[instance].usage[type].usage || 0
                }
            }
            return otherUsage
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['currentSpaceType']),
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        },
        resolvedSpaceType() {
            return this.spaceType !== null ? this.spaceType : this.currentSpaceType
        },
        isEducationSpace() {
            return this.resolvedSpaceType === this.spaceTypes.EDUCATION_SPACE
        }
    },
    watch: {
        spaceId() {
            this.fetchAll()
        }
    }
}
</script>
